import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import useFetch from '../../hooks/useFetch';
import { getUnitatColor } from '../../utils/unitats';
import { axiosInstance } from '../../config/axios';

// ─── Styled components ─────────────────────────────────────────────────────

const ResultsContainer = styled.div`
	background: white;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	padding: 16px 20px;
	margin-bottom: 20px;
`;

const ResultsHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 12px;
`;

const ResultsTitle = styled.h3`
	margin: 0;
	font-size: 1.1em;
	color: #2c3e50;
`;

const SummaryGrid = styled.div`
	display: flex;
	gap: 16px;
	flex-wrap: wrap;
	margin-bottom: 12px;
`;

const SummaryCard = styled.div`
	flex: 1;
	min-width: 140px;
	background: #f8f9fa;
	border-radius: 8px;
	padding: 12px 16px;
	text-align: center;
	border: 1px solid #e9ecef;
`;

const SummaryLabel = styled.div`
	font-size: 0.75em;
	color: #888;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	margin-bottom: 4px;
`;

const SummaryValue = styled.div`
	font-size: 1.5em;
	font-weight: 700;
	color: #2c3e50;
`;

const SummarySub = styled.div`
	font-size: 0.85em;
	color: #888;
	margin-top: 2px;
`;

const TopUnitats = styled.div`
	margin-bottom: 16px;
`;

const TopUnitatRow = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 8px 0;
	border-bottom: 1px solid #f0f0f0;

	&:last-child {
		border-bottom: none;
	}
`;

const UnitatBadge = styled.span<{ $unitat: string }>`
	display: inline-block;
	background: ${({ $unitat }) => getUnitatColor($unitat)};
	color: white;
	padding: 4px 10px;
	border-radius: 4px;
	font-size: 0.8em;
	font-weight: 600;
	min-width: 80px;
	text-align: center;
`;

const WeightBar = styled.div<{ $width: number; $color: string }>`
	height: 10px;
	border-radius: 5px;
	background: ${({ $color }) => $color};
	width: ${({ $width }) => Math.max($width, 2)}%;
	min-width: 2px;
	transition: width 0.3s ease;
`;

const WeightBarContainer = styled.div`
	flex: 1;
	background: #e9ecef;
	border-radius: 5px;
	height: 10px;
`;

const WeightValue = styled.span`
	font-size: 0.85em;
	font-weight: 600;
	color: #555;
	min-width: 36px;
	text-align: right;
`;

const TopYears = styled.div`
	margin-bottom: 12px;
`;

const YearChips = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
`;

const YearChip = styled.span`
	background: #e8f4fd;
	color: #2c6b9e;
	padding: 4px 12px;
	border-radius: 12px;
	font-size: 0.85em;
	font-weight: 500;
	border: 1px solid #b8d4ed;
`;

const YearChipCount = styled.span`
	font-weight: 700;
	color: #1a5276;
`;

const IndividualList = styled.div`
	margin-top: 12px;
`;

const IndividualRow = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 10px 0;
	border-bottom: 1px solid #f0f0f0;

	&:last-child {
		border-bottom: none;
	}
`;

const RespondentName = styled.span`
	font-weight: 600;
	color: #2c3e50;
	min-width: 80px;
	font-size: 0.9em;
`;

const ConfidenceDot = styled.div<{ $confidence: number }>`
	width: 12px;
	height: 12px;
	border-radius: 50%;
	background: ${({ $confidence }) =>
		$confidence >= 80
			? '#27ae60'
			: $confidence >= 50
			? '#f39c12'
			: '#e74c3c'};
	flex-shrink: 0;
`;
const DeleteButton = styled.button`
	background: none;
	border: none;
	color: #e74c3c;
	cursor: pointer;
	font-size: 1.1em;
	padding: 2px 6px;
	border-radius: 4px;
	transition: all 0.2s ease;
	line-height: 1;

	&:hover {
		background: #fce4e4;
	}

	&:disabled {
		opacity: 0.3;
		cursor: not-allowed;
	}
`;


const YearDisplay = styled.span`
	font-size: 0.85em;
	color: #555;
`;

const AllResponsesToggle = styled.button`
	background: none;
	border: none;
	color: #3498db;
	cursor: pointer;
	font-size: 0.85em;
	font-weight: 500;
	padding: 0;
	text-decoration: underline;

	&:hover {
		color: #2980b9;
	}
`;

const WaitingMessage = styled.div`
	background: #fff3cd;
	border: 1px solid #ffc107;
	border-radius: 6px;
	padding: 10px 14px;
	color: #856404;
	font-size: 0.9em;
	margin-bottom: 12px;
	text-align: center;
`;

// ─── Types ─────────────────────────────────────────────────────────────────

interface Classification {
	_id: string;
	unitat: string;
	schoolYear: string;
	confidence: number;
	respondentLabel?: string;
	fingerprint: string;
	createdAt: string;
}

interface ClassificationResultsProps {
	albumId: string;
}

// ─── Component ─────────────────────────────────────────────────────────────

const ClassificationResults: React.FC<ClassificationResultsProps> = ({
	albumId,
}) => {
	const queryClient = useQueryClient();
	const [showAll, setShowAll] = React.useState(false);
	const [deletingId, setDeletingId] = useState<string | null>(null);

	const handleDelete = async (classificationId: string) => {
		if (!window.confirm('Eliminar aquesta classificació?')) return;
		setDeletingId(classificationId);
		try {
			await axiosInstance.delete(`/api/galeria/albums/${albumId}/classifications/${classificationId}`);
			// Invalidate to refresh the list immediately
			queryClient.invalidateQueries({
				queryKey: ['classifications', albumId],
			});
			queryClient.invalidateQueries({
				queryKey: ['classification-overview'],
			});
		} catch (err) {
			console.error('Error deleting classification:', err);
			alert('Error en eliminar la classificació');
		} finally {
			setDeletingId(null);
		}
	};

	const { data, isLoading } = useFetch<{
		classifications: Classification[];
	}>({
		queryKey: ['classifications', albumId],
		url: `/api/galeria/albums/${albumId}/classifications`,
	});

	const classifications = data?.classifications || [];

	// Compute weighted unitat scores
	const unitatScores = useMemo(() => {
		const scores = new Map<
			string,
			{ totalWeight: number; count: number }
		>();

		for (const c of classifications) {
			const existing = scores.get(c.unitat) || {
				totalWeight: 0,
				count: 0,
			};
			existing.totalWeight += c.confidence;
			existing.count += 1;
			scores.set(c.unitat, existing);
		}

		return Array.from(scores.entries())
			.map(([unitat, data]) => ({
				unitat,
				weightedScore: data.totalWeight / data.count,
				count: data.count,
			}))
			.sort((a, b) => b.weightedScore - a.weightedScore);
	}, [classifications]);

	const maxScore = useMemo(
		() =>
			unitatScores.length > 0
				? Math.max(...unitatScores.map((s) => s.weightedScore))
				: 1,
		[unitatScores],
	);

	// Count by school year
	const yearCounts = useMemo(() => {
		const counts = new Map<string, number>();
		for (const c of classifications) {
			counts.set(c.schoolYear, (counts.get(c.schoolYear) || 0) + 1);
		}
		return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
	}, [classifications]);

	if (isLoading) return null;

	return (
		<ResultsContainer>
			<ResultsHeader>
				<ResultsTitle>
					Classificacions rebudes ({classifications.length})
				</ResultsTitle>
				{classifications.length > 0 && (
					<AllResponsesToggle onClick={() => setShowAll(!showAll)}>
						{showAll
							? 'Veure resum'
							: 'Veure respostes individuals'}
					</AllResponsesToggle>
				)}
			</ResultsHeader>

			{classifications.length === 0 ? (
				<WaitingMessage>
					Encara no s'han rebut classificacions per a aquest àlbum.
				</WaitingMessage>
			) : showAll ? (
				/* ─── Individual list view ─── */
				<IndividualList>
					{classifications.map((c) => (
						<IndividualRow key={c._id}>
							<ConfidenceDot $confidence={c.confidence} />
							<UnitatBadge $unitat={c.unitat}>
								{c.unitat === 'agrupament'
									? 'Agrupament'
									: c.unitat}
							</UnitatBadge>
							<YearDisplay>{c.schoolYear}</YearDisplay>
							<RespondentName>
								{c.respondentLabel || 'Anònim'}
							</RespondentName>
							<span
								style={{
									marginLeft: 'auto',
									fontWeight: 600,
									color:
										c.confidence >= 80
											? '#27ae60'
											: c.confidence >= 50
											? '#f39c12'
											: '#e74c3c',
								}}
							>
								{c.confidence}%
							</span>
							<DeleteButton
								title="Eliminar aquesta classificació"
								onClick={() => handleDelete(c._id)}
								disabled={deletingId === c._id}
							>
								{deletingId === c._id ? '...' : '×'}
							</DeleteButton>
						</IndividualRow>
					))}
				</IndividualList>
			) : (
				/* ─── Summary view ─── */
				<>
					{/* Summary cards */}
					<SummaryGrid>
						<SummaryCard>
							<SummaryLabel>Total respostes</SummaryLabel>
							<SummaryValue>
								{classifications.length}
							</SummaryValue>
							<SummarySub>
								{unitatScores.length > 0
									? unitatScores[0].count ===
									  classifications.length
										? 'Tothom coincideix'
										: `${unitatScores[0].count} de ${classifications.length} coincideixen`
									: ''}
							</SummarySub>
						</SummaryCard>
						<SummaryCard>
							<SummaryLabel>Unitat més votada</SummaryLabel>
							<SummaryValue>
								{unitatScores.length > 0
									? unitatScores[0].unitat === 'agrupament'
										? 'Agrupament'
										: unitatScores[0].unitat
									: '—'}
							</SummaryValue>
							<SummarySub>
								{unitatScores.length > 0
									? `Puntuació: ${Math.round(
											unitatScores[0].weightedScore,
									  )}% · ${unitatScores[0].count} vots`
									: ''}
							</SummarySub>
						</SummaryCard>
						<SummaryCard>
							<SummaryLabel>Curs més votat</SummaryLabel>
							<SummaryValue>
								{yearCounts.length > 0
									? yearCounts[0][0]
									: '—'}
							</SummaryValue>
							<SummarySub>
								{yearCounts.length > 0
									? `${yearCounts[0][1]} vots`
									: ''}
							</SummarySub>
						</SummaryCard>
						<SummaryCard>
							<SummaryLabel>Confiança mitjana</SummaryLabel>
							<SummaryValue>
								{classifications.length > 0
									? `${Math.round(
											classifications.reduce(
												(sum, c) => sum + c.confidence,
												0,
											) / classifications.length,
									  )}%`
									: '—'}
							</SummaryValue>
						</SummaryCard>
					</SummaryGrid>

					{/* Unitat breakdown with weighted bars */}
					{unitatScores.length > 1 && (
						<TopUnitats>
							<SummaryLabel style={{ marginBottom: 8 }}>
								Per unitat (ponderat per confiança)
							</SummaryLabel>
							{unitatScores.map((s) => (
								<TopUnitatRow key={s.unitat}>
									<UnitatBadge $unitat={s.unitat}>
										{s.unitat === 'agrupament'
											? 'Agrupament'
											: s.unitat}
									</UnitatBadge>
									<WeightBarContainer>
										<WeightBar
											$width={
												(s.weightedScore / maxScore) *
												100
											}
											$color={getUnitatColor(s.unitat)}
										/>
									</WeightBarContainer>
									<WeightValue>
										{Math.round(s.weightedScore)}%
									</WeightValue>
									<span
										style={{
											fontSize: '0.8em',
											color: '#888',
										}}
									>
										({s.count} {s.count === 1 ? 'vot' : 'vots'}
										)
									</span>
								</TopUnitatRow>
							))}
						</TopUnitats>
					)}

					{/* School year breakdown */}
					{yearCounts.length > 1 && (
						<TopYears>
							<SummaryLabel style={{ marginBottom: 8 }}>
								Per curs
							</SummaryLabel>
							<YearChips>
								{yearCounts.map(([year, count]) => (
									<YearChip key={year}>
										{year}{' '}
										<YearChipCount>({count})</YearChipCount>
									</YearChip>
								))}
							</YearChips>
						</TopYears>
					)}
				</>
			)}
		</ResultsContainer>
	);
};

export default ClassificationResults;
